import { BasicType, Pageable } from '@/service/commonType';
import { useTokenStore } from '@/store/authToken';
import { appToLogout } from '@/utils/js/webView';

function removeEmptyParams<T extends Record<string, any>>(obj: T): Partial<T> {
  return Object.fromEntries(
    Object.entries(obj).filter(([_, value]) => value !== '' && value !== undefined),
  ) as Partial<T>;
}

export const baseUrl =
  globalThis.window === undefined
    ? process.env.TOWN_API_URL
    : '/api';

export async function customFetch<T>(
  path: string,
  originalOptions: RequestInit & { params?: Record<string, any> } = {},
  retryCount = 0,
): Promise<T> {
  const options = { ...originalOptions };

  let url = `${baseUrl}${path}`;
  const { token: authToken, setToken } = useTokenStore.getState();

  const defaultHeaders = {
    'Content-Type': 'application/json',
    ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
    // Authorization:
    //   'Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJuTWVtYmVyQ29kZSI6NDAwMCwidXNlckFnZW50IjoiSVBIT05FMTMiLCJpYXQiOjE3MjYxMDIxNjEsImV4cCI6MjAyNjE4ODU2MX0.ecmq0PHjNpPdpVPLSDFc32ib6KoaQE7KSXELkGc0t9c',
  };

  // URL 쿼리 파라미터 처리
  if (options.params) {
    const cleanParams = removeEmptyParams(options.params);
    const searchParams = new URLSearchParams(cleanParams as Record<string, string>);
    url += '?' + searchParams.toString();
    delete options.params; // options에서 queryParams 제거
  }

  // Body 데이터 처리 (POST 요청 등)
  if (
    options.headers &&
    (options.headers as Record<string, string>)['Content-Type'] !== 'multipart/form-data' &&
    options.body &&
    typeof options.body === 'object'
  ) {
    options.body = JSON.stringify(removeEmptyParams(options.body as Record<string, any>));
  }

  const mergedHeaders = { ...defaultHeaders, ...options.headers };

  if ((mergedHeaders as Record<string, string>)['Content-Type'] === 'multipart/form-data') {
    delete (mergedHeaders as Record<string, string>)['Content-Type']; // Content-Type 헤더를 삭제하여 브라우저가 자동으로 설정하도록 함
  }

  try {
    const response = await fetch(url, {
      ...options,
      headers: mergedHeaders,
    });
    if (!response.ok) {
      const errorData = await response.json();
      const error = new Error(`HTTP error! status: ${response.status}`);
      (error as any).status = response.status;
      (error as any).data = errorData;
      throw error;
    }
    return response.json();
  } catch (error) {
    console.error('Error:', error);
    if ((error as any).status === 401 && retryCount === 0) {
      setToken(null);
      if (globalThis.window?.webkit || globalThis.window?.Android) {
        appToLogout();
      }
      return customFetch<T>(path, originalOptions, retryCount + 1);
    }
    throw error;
  }
}

interface LastPageParam extends BasicType {
  data: {
    items: any[];
    pageable: Pageable;
  };
}

export const basicInfiniteQueryOptions = {
  initialPageParam: 1,
  getNextPageParam: (lastPage: LastPageParam, pages: LastPageParam[]) => {
    // 최신 데이터 paging totalCount
    const { page, totalCount } = lastPage.data.pageable;
    // 현재 노출된 data length
    const currentDataLength = pages.flatMap((e) => e.data.items).length;

    if (currentDataLength >= totalCount) {
      return undefined;
    }
    return page + 1;
  },
};
